import { Input, Upload, type UploadFile } from 'antd';
import { Button, Form, Modal, requiredTextRule } from 'components';
import type { VFC } from 'react';
import { useUserInfo } from '@/hooks/useUser';
import {
  type CreatePersonDto,
  createPersonDtoSchema,
  type SerializedPerson,
  updatePersonDtoSchema,
} from '@enmon/commons/lib/tools';
import { useUpdatePerson } from 'api/hooks/person/useUpdate';
import { useCreatePerson } from 'api/hooks/person/useCreate';
import { isNil } from 'ramda';
import { Icon } from '@/components/icon';
import { PERSONS_FORM_ID } from '../constants';

interface PersonModalProps {
  person?: SerializedPerson;
  onClose: () => void;
  onSuccess: (person: SerializedPerson) => void;
}

type PersonFormValues = Partial<
  Pick<CreatePersonDto, 'name' | 'contact' | 'email' | 'position'> & {
    photo: UploadFile<File>;
  }
>;

export const PersonModal: VFC<PersonModalProps> = ({ person, onClose, onSuccess }) => {
  const { id } = person ?? {};
  const [form] = Form.useForm<PersonFormValues>();
  const { reseller } = useUserInfo();
  const { mutate: updatePerson, isLoading: isUpdating } = useUpdatePerson({ onSuccess });
  const { mutate: createPerson, isLoading: isCreating } = useCreatePerson({ onSuccess });

  const isEditingPerson = !isNil(id);

  const handleFormSubmit = (values: PersonFormValues) => {
    if (isEditingPerson) {
      const parsedValues = updatePersonDtoSchema.safeParse({ ...person, ...values });

      if (!parsedValues.success) {
        console.error(parsedValues.error);
        return;
      }
      updatePerson({ id, values: { ...parsedValues.data, photo: values.photo } });
    } else {
      const parsedValues = createPersonDtoSchema.safeParse({ ...values, reseller: reseller?.id, photo_drive_id: null });

      if (!parsedValues.success) {
        console.error(parsedValues.error);
        return;
      }
      createPerson({ ...parsedValues.data, photo: values.photo });
    }
  };

  return (
    <Modal
      centered
      open
      cancelButtonProps={{ onClick: onClose }}
      confirmLoading={isCreating || isUpdating}
      maskClosable={false}
      okButtonProps={{
        htmlType: 'submit',
        form: PERSONS_FORM_ID,
      }}
      title={`${isEditingPerson ? 'Edit' : 'Add new'} person`}
      onCancel={onClose}
    >
      <Form form={form} id={PERSONS_FORM_ID} initialValues={person} layout="vertical" onFinish={handleFormSubmit}>
        <Form.Item label="Name of person" name={'name' satisfies keyof PersonFormValues} rules={[requiredTextRule]}>
          <Input placeholder="John Doe" />
        </Form.Item>

        <Form.Item label="Contact" name={'contact' satisfies keyof PersonFormValues} rules={[requiredTextRule]}>
          <Input placeholder="+420 123 456 789" />
        </Form.Item>

        <Form.Item label="Email" name={'email' satisfies keyof PersonFormValues} rules={[requiredTextRule]}>
          <Input placeholder="jhon.doe@example.com" type="email" />
        </Form.Item>

        <Form.Item label="Position" name={'position' satisfies keyof PersonFormValues} rules={[requiredTextRule]}>
          <Input placeholder="Accountant" />
        </Form.Item>

        {/* This hidden field is needed, otherwise antd doesn`t include photo field in submitted values */}
        <Form.Item hidden name={'photo' satisfies keyof PersonFormValues} />
        <Upload
          showUploadList
          accept="image/*"
          beforeUpload={() => false}
          listType="picture"
          maxCount={1}
          multiple={false}
          onChange={({ fileList }) => form.setFieldsValue({ photo: fileList.length > 0 ? fileList[0] : undefined })}
        >
          <Button icon={isUpdating ? <Icon iconString="spinner" /> : <Icon iconString="upload" />} type="primary">
            Select Photo
          </Button>
        </Upload>
      </Form>
    </Modal>
  );
};
